import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
  CostAllocationTypeConstants,
  SettingsKey,
} from '@app/core/_constant/settings.constants';
import { environment } from '@app/env-service';
import { ISettings } from '@app/interfaces/settings.interface';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  url :string = environment.url;
  lastUpdate :number; // = +new Date('2019-09-09');
  params :any = {};

  productTypes :any[] = [
    { id: 1, text: 'New Product' },
    { id: 2, text: 'Inventory' },
    { id: 3, text: 'Quotes' },
  ];

  productDepartments :any[] = [
    { id: -1, text: 'N/A' },
    { id: 1, text: 'Embroidery' },
    { id: 2, text: 'Dye sub' },
    { id: 3, text: 'Direct to garment' },
    { id: 4, text: 'Laser' },
    { id: 5, text: 'Diamond' },
    { id: 6, text: 'UV' },
    { id: 7, text: 'Fiber' },
    { id: 8, text: 'Heat stamp' },
  ];

  gettingRecords :boolean;

  private readonly _settings = new BehaviorSubject<any>(<any>{});
  public settings$ = this._settings.asObservable();

  private readonly _masterfile = new BehaviorSubject<any>(<any>{});
  public masterfile$ = this._masterfile.asObservable();

  private readonly _paymentTerms = new BehaviorSubject<any>(<any>{});
  public paymentTerms$ = this._paymentTerms.asObservable();

  private readonly _inventorySettings = new BehaviorSubject<any>(<any>{});
  public inventorySettings$ = this._inventorySettings.asObservable();

  private readonly _trackInventoryByLocationValue = new BehaviorSubject<any>(
    <any>{},
  );
  public trackInventoryByLocationValue$ =
    this._trackInventoryByLocationValue.asObservable();

  LOGISTICS :any[] = [];
  cachedKeyValueSettings :ISettings[] = [];
  constructor(private http :HttpClient) {
    this.getSettings();
    this.getLogisticsSettings();
    this.getPaymentTerms();
    this.getInventorySettingValues();
    this.getTrackInventoryByLocationValues();
    this.setDefaultCostAllocationType();
  }

  private setDefaultCostAllocationType() :void {
    this.getKeyValue(SettingsKey.COST_ALLOCATION_TYPE).subscribe((resp :any) => {
      if (!resp) {
        this.setKeyValue(
          SettingsKey.COST_ALLOCATION_TYPE,
          CostAllocationTypeConstants.VALUE_BASED_ALLOCATION,
        ).subscribe();
      }
    });
  }

  getLogistics() {
    const settings :any = this._masterfile.getValue();

    return settings.LOGISTICS;
  }

  setCachedKeyValueSettings(key :string, value :any) :void {
    this.cachedKeyValueSettings[key] = value;
  }

  getCachedKeyValueSettings(key :string) :Observable<any> {
    if (this.cachedKeyValueSettings[key]) {
      return of(this.cachedKeyValueSettings[key]);
    }

    return this.getKeyValue(key).pipe(
      tap((resp :any) => {
        this.setCachedKeyValueSettings(key, resp.value);
      }),
    );
  }

  getKeyValue(key :string) :Observable<any> {
    return this.http.get(`${this.url}/settings/keyvalue`, {
      params: { key: key },
    });
  }

  setKeyValue(key :string, value :any) :Observable<any> {
    return this.http.post(`${this.url}/settings/keyvalue`, {
      key: key,
      value: value,
    });
  }

  getLogisticsSettings() {
    this.http.get(`${this.url}/settings/logistics`).subscribe((resp :any) => {
      this._masterfile.next(resp);
    });
  }

  getSettings() {
    this.gettingRecords = true;

    return this.http.get(`${this.url}/settings`).subscribe((resp :any) => {
      const settings :any = {};
      resp.forEach((row) => {
        settings[row.key] = row.value;
        try {
          settings[row.key] = JSON.parse(row.value);
        } catch (e) {}
      });
      this._settings.next(settings);
      this.gettingRecords = false;
    });
  }

  saveSettings(data :any) :Observable<any> {
    return this.http.post(`${this.url}/settings`, data).pipe(
      tap((resp :any) => {
        const settings :any = {};
        settings.payment_terms = JSON.parse(data.payment_terms);
        settings.mark_up_structure = JSON.parse(data.mark_up_structure);
        settings.department_leadtimes = JSON.parse(data.department_leadtimes);
        settings.labor_costs = JSON.parse(data.labor_costs);
        this._settings.next(settings);
      }),
    );
  }

  getSettingsValue() {
    return this._settings.getValue();
  }

  getLeadTime(logistic :string, prod_lead_time :number) {
    const settings = this.getSettingsValue();
    if (logistic == 'exw') {
      return prod_lead_time;
    } else if (logistic == 'ddp_sea') {
      return parseInt(settings['shipping_sea']) + prod_lead_time;
    } else if (logistic == 'ddp_air') {
      return parseInt(settings['shipping_air']) + prod_lead_time;
    } else if (logistic == 'fob') {
      return parseInt(settings['shipping_fob']) + prod_lead_time;
    } else if (logistic == 'cif') {
      return parseInt(settings['shipping_cif']) + prod_lead_time;
    }
  }

  getFilters() :Observable<any> {
    return this.http.get(`${this.url}/settings/filters`);
  }

  savePOSettings(payload) {
    return this.http.post(`${this.url}/settings/purchase_orders`, payload);
  }

  getPOSettings() {
    return this.http.get(`${this.url}/settings/purchase_orders`);
  }

  saveInventorySettings(payload) {
    return this.http.post(`${this.url}/settings/inventory`, payload);
  }

  getInventorySettings() {
    return this.http.get(`${this.url}/settings/inventory`);
  }

  saveSalesPurgatorySettings(payload) {
    return this.http.post(`${this.url}/settings/sales_purgatory`, payload);
  }

  getSalesPurgatorySettings() {
    return this.http.get(`${this.url}/settings/sales_purgatory`);
  }

  saveProjectManagementSettings(payload) {
    return this.http.post(`${this.url}/settings/project-management`, payload);
  }

  getProjectManagementSettings() {
    return this.http.get(`${this.url}/settings/project-management`);
  }

  saveB2bPortalSettings(payload) {
    return this.http.post(`${this.url}/settings/b2b-portal`, payload);
  }

  getB2bPortalSettings() {
    return this.http.get(`${this.url}/settings/b2b-portal`);
  }

  savePicklistSettings(payload) {
    return this.http.post(`${this.url}/settings/picklist`, payload);
  }

  saveMyProfileSettings(payload) :any {
    return this.http.post(`${this.url}/settings/my-profile`, payload);
  }

  getMyProfileSettings() :Observable<any> {
    return this.http.get(`${this.url}/settings/my-profile`);
  }

  getProfileLogo(path) :Observable<any> {
    return this.http.get(`${this.url}/settings/profile-logo?filename=` + path);
  }

  getPaymentTerms() :void {
    this.http.get(`${this.url}/settings/payment_terms`).subscribe((resp :any) => {
      this._paymentTerms.next(resp);
    });
  }

  saveSkuSettings(payload) :any {
    return this.http.post(`${this.url}/settings/sku`, payload);
  }

  getSkuSettings() :Observable<any> {
    return this.http.get(`${this.url}/settings/sku`);
  }

  saveInvoiceSettings(payload) :any {
    return this.http.post(`${this.url}/settings/invoice`, payload);
  }

  getInvoiceSettings() :Observable<any> {
    return this.http.get(`${this.url}/settings/invoice`);
  }

  getInventorySettingValues() :Subscription {
    return this.http
      .get(`${this.url}/settings/inventory`)
      .subscribe((resp :any) => {
        this._inventorySettings.next(resp);
      });
  }

  getTrackInventoryByLocationValues() :Subscription {
    return this.http
      .get(`${this.url}/settings/keyvalue`, {
        params: { key: 'track_inventory_by_location' },
      })
      .subscribe((resp :any) => {
        this._trackInventoryByLocationValue.next(resp);
      });
  }

  getQuickbookSettings() :Observable<any> {
    return this.http.get(`${this.url}/settings/quickbook`);
  }

  saveQuickbookSettings(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/settings/quickbook`, payload);
  }
}
